/**
 * EmailAdvancedFiltersPopover.jsx — Wave 1u3 Phase D / I1
 *
 * Combined filters bar with: tags, kanban status, assignee, date range.
 * All criteria are AND-combined and sent to the server as query params.
 *
 * Props:
 *   - open: boolean
 *   - onClose: () => void
 *   - filters: { tags: [], status: '', assignee: '', since: '', until: '' }
 *   - onApply: (newFilters) => void
 *   - team: [{ user_id, name, email }]
 */
import React, { useEffect, useState } from 'react';
import { Filter, X, Tag, Loader2, Trash2 } from 'lucide-react';
import api from '../../api/client';

const STATUSES = [
  { value: '', label: '— Tous —' },
  { value: 'to_handle', label: 'À traiter' },
  { value: 'in_progress', label: 'En cours' },
  { value: 'done', label: 'Traité' },
  { value: 'archived', label: 'Archivé' },
];

export default function EmailAdvancedFiltersPopover({
  open,
  onClose,
  filters,
  onApply,
  team = [],
}) {
  const [tags, setTags] = useState(filters?.tags || []);
  const [status, setStatus] = useState(filters?.status || '');
  const [assignee, setAssignee] = useState(filters?.assignee || '');
  const [since, setSince] = useState(filters?.since || '');
  const [until, setUntil] = useState(filters?.until || '');
  const [orgTags, setOrgTags] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTags(filters?.tags || []);
    setStatus(filters?.status || '');
    setAssignee(filters?.assignee || '');
    setSince(filters?.since || '');
    setUntil(filters?.until || '');
    setLoading(true);
    api.get('/mailbox/tags')
      .then(({ data }) => setOrgTags(data?.tags || []))
      .catch(() => setOrgTags([]))
      .finally(() => setLoading(false));
  }, [open, filters]);

  const toggleTag = (name) => {
    setTags((prev) => prev.includes(name) ? prev.filter((t) => t !== name) : [...prev, name]);
  };

  const apply = () => {
    onApply?.({
      tags,
      status,
      assignee,
      since,
      until,
    });
    onClose?.();
  };

  const reset = () => {
    setTags([]); setStatus(''); setAssignee(''); setSince(''); setUntil('');
  };

  const hasAny = tags.length > 0 || status || assignee || since || until;

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/40" onClick={onClose} data-testid="email-filters-popover">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <header className="px-4 py-3 border-b border-[#EFF0F6] flex items-center justify-between">
          <h3 className="text-[14px] font-bold text-[#1A1B2E] flex items-center gap-2">
            <Filter className="w-4 h-4 text-[#4F46E5]" /> Filtres avancés
          </h3>
          <button onClick={onClose} className="text-[#6B6C85] hover:text-[#1A1B2E]" data-testid="email-filters-close">
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Tags */}
          <div data-testid="email-filters-tags">
            <div className="text-[11px] font-semibold text-[#6B6C85] uppercase tracking-wide mb-2 flex items-center gap-1">
              <Tag className="w-3 h-3" /> Tags (le mail doit contenir TOUS les tags cochés)
            </div>
            {loading ? (
              <div className="text-[11.5px] text-[#8E8FA5]"><Loader2 className="w-3 h-3 animate-spin inline-block mr-1" /> Chargement…</div>
            ) : orgTags.length === 0 ? (
              <div className="text-[11.5px] text-[#8E8FA5] italic">Aucun tag dans votre org pour l'instant.</div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {orgTags.map((t) => {
                  const active = tags.includes(t.name);
                  return (
                    <button
                      key={t.name}
                      type="button"
                      onClick={() => toggleTag(t.name)}
                      data-testid={`email-filters-tag-${t.name}`}
                      className={`px-2 py-0.5 rounded text-[11px] font-semibold border ${active ? 'bg-[#4F46E5] text-white border-[#4F46E5]' : 'bg-white text-[#4F46E5] border-[#C7D2FE] hover:bg-[#EEF2FF]'}`}
                    >
                      #{t.name} <span className="opacity-60 text-[9.5px]">({t.count})</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Status */}
          <div data-testid="email-filters-status">
            <div className="text-[11px] font-semibold text-[#6B6C85] uppercase tracking-wide mb-2">Statut Kanban</div>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              data-testid="email-filters-status-select"
              className="w-full h-8 px-2 text-[12.5px] bg-white border border-[#EFF0F6] rounded focus:outline-none focus:border-[#4F46E5]"
            >
              {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>

          {/* Assignee */}
          <div data-testid="email-filters-assignee">
            <div className="text-[11px] font-semibold text-[#6B6C85] uppercase tracking-wide mb-2">Assigné à</div>
            <select
              value={assignee}
              onChange={(e) => setAssignee(e.target.value)}
              data-testid="email-filters-assignee-select"
              className="w-full h-8 px-2 text-[12.5px] bg-white border border-[#EFF0F6] rounded focus:outline-none focus:border-[#4F46E5]"
            >
              <option value="">— Indifférent —</option>
              <option value="me">Moi</option>
              <option value="none">Personne (non assigné)</option>
              {team.map((m) => (
                <option key={m.user_id} value={m.user_id}>{m.name || m.email || m.user_id}</option>
              ))}
            </select>
          </div>

          {/* Date range */}
          <div data-testid="email-filters-dates">
            <div className="text-[11px] font-semibold text-[#6B6C85] uppercase tracking-wide mb-2">Période</div>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={since}
                onChange={(e) => setSince(e.target.value)}
                data-testid="email-filters-since"
                className="flex-1 h-8 px-2 text-[12px] bg-white border border-[#EFF0F6] rounded focus:outline-none focus:border-[#4F46E5]"
                placeholder="Du"
              />
              <span className="text-[11px] text-[#8E8FA5]">→</span>
              <input
                type="date"
                value={until}
                onChange={(e) => setUntil(e.target.value)}
                data-testid="email-filters-until"
                className="flex-1 h-8 px-2 text-[12px] bg-white border border-[#EFF0F6] rounded focus:outline-none focus:border-[#4F46E5]"
                placeholder="Au"
              />
            </div>
          </div>
        </div>

        <footer className="px-4 py-3 border-t border-[#EFF0F6] flex items-center justify-between bg-[#FAFAFE]">
          <button
            type="button"
            onClick={reset}
            disabled={!hasAny}
            data-testid="email-filters-reset"
            className="h-8 px-3 rounded text-[11.5px] font-semibold text-[#6B6C85] hover:bg-white disabled:opacity-40 inline-flex items-center gap-1"
          >
            <Trash2 className="w-3 h-3" /> Réinitialiser
          </button>
          <button
            type="button"
            onClick={apply}
            data-testid="email-filters-apply"
            className="h-8 px-4 rounded-lg brand-gradient text-white text-[12px] font-semibold inline-flex items-center gap-1.5"
          >
            <Filter className="w-3.5 h-3.5" /> Appliquer
          </button>
        </footer>
      </div>
    </div>
  );
}
